import { useCallback } from 'react';
import { Routes } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { useDevice, useModal } from '@/hooks';
import { Flex, LoadingSpin } from '@/components/common';
import HelpdeskCloseModal from './HelpdeskCloseModal';
import HelpdeskCancelModal from './HelpdeskCancelModal';
import HelpdeskAddNoteModal from './HelpdeskAddNoteModal';
import HelpdeskIssueDesktop from './HelpdeskIssueDesktop';
import HelpdeskIssueMobile from './HelpdeskIssueMobile';

export interface HelpdeskIssueProps {
  issue: RedmineIssueEntity;
  error: string;
  retry: () => any;
  loading?: boolean;
  addNoteModal: ReturnType<typeof useModal>;
  closeIssueModal: ReturnType<typeof useModal>;
  cancelIssueModal: ReturnType<typeof useModal>;
}

const sharedStyles = {
  leftSideFlex: '150px',
  circle: 'rounded-full w-[17px] h-[17px] min-w-[17px] min-h-[17px] border-2 border-solid border-helpdesk',
  rowContainer: 'text-base border-0 border-b border-solid border-helpdesk pb-6 mb-3',
  infoRow: 'flex h-full text-helpdesk',
  infoKey: 'w-[50%] pt-2 pl-3 inline-flex items-center justify-end font-bold border-0 border-l-2 border-solid border-helpdesk',
  infoVal: 'w-[50%] pt-2 pr-3 inline-flex items-center',
  box: {
    base: 'h-full bg-[#F7E4AF] p-3 rounded-xl',
    title: 'text-helpdesk font-semibold border-0 border-b-2 border-solid border-b-helpdesk pb-2'
  }
};

function HelpdeskIssue({
  issue,
  error,
  retry,
  loading,
  addNoteModal,
  closeIssueModal,
  cancelIssueModal
}: HelpdeskIssueProps) {

  const navigate = useNavigate();
  const { isDesktop } = useDevice();

  const redirectToHelpdesk = useCallback(() => {
    navigate(Routes.HelpdeskViewIssue);
  }, [navigate]);

  if (loading) {
    return <LoadingSpin className="text-helpdesk" fillHeight={!isDesktop()} />;
  }

  if (error) {
    return <>Error</>;
  }

  return (
    <Flex direction="column">
      <HelpdeskAddNoteModal onComplete={retry} issue={issue} {...addNoteModal} />
      <HelpdeskCloseModal onComplete={redirectToHelpdesk} issue={issue} {...closeIssueModal} />
      <HelpdeskCancelModal onComplete={redirectToHelpdesk} issue={issue} {...cancelIssueModal} />
      {isDesktop() ?
        <HelpdeskIssueDesktop
          issue={issue}
          styles={sharedStyles}
          addNoteModal={addNoteModal}
          closeIssueModal={closeIssueModal}
          cancelIssueModal={cancelIssueModal}
        /> :
        <HelpdeskIssueMobile
          issue={issue}
          styles={sharedStyles}
          addNoteModal={addNoteModal}
        />
      }

    </Flex>
  );
}

export default HelpdeskIssue;
